import { useQueryClient } from '@tanstack/react-query'
import { Button, Col, DatePicker, Flex, Form, Modal, Row } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { FormInstance } from 'antd/lib'

import {
  usePersonsServiceGetPersonListByDataEndpointKey,
  useTreatmentServiceCreatePotentialContactEndpoint,
  useTreatmentServiceGetListPotentialContactEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { SaveConfirm } from 'src/components'
import { disabledFutureDate, formatDate, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from '../TutorForm/styles'

type Props = {
  onModalCancel: () => void
  onCancel?: () => void
  form?: FormInstance
  personId: string
  consultationId: string
}

export const ContactsAddForm = ({
  onModalCancel,
  onCancel,
  form: formConsultations,
  personId,
  consultationId
}: Props) => {
  const { t } = useLocales()

  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { styles } = useStyles()

  const { mutate: addContacts } =
    useTreatmentServiceCreatePotentialContactEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetListPotentialContactEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [usePersonsServiceGetPersonListByDataEndpointKey]
        })

        onCancel?.()
        onModalCancel()
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onAddContacts = async () => {
    const values = await form.validateFields()

    if (formConsultations) {
      const contacts = formConsultations.getFieldValue('contactsList') ?? []

      const newContact = {
        personId: String(personId),
        contactDate: values.contactDate
          ? formatDate(values.contactDate, yearMonthDaySlash)
          : undefined,
        comments: values.comments
      }

      formConsultations.setFieldValue('contactsList', [...contacts, newContact])

      onCancel?.()
      onModalCancel()
      return
    }

    const requestBody = {
      contacts: [
        {
          personId: String(personId),
          contactDate:
            values.contactDate &&
            formatDate(values.contactDate, yearMonthDaySlash),
          comments: values.comments
        }
      ]
    }

    addContacts({ consultationId: String(consultationId), requestBody })
  }

  return (
    <Modal
      open
      width={600}
      maskClosable={false}
      title={t('consultations.contacts_add')}
      centered
      footer={false}
      onCancel={onModalCancel}
    >
      <Form requiredMark={false} layout="vertical" form={form}>
        <Flex vertical gap={24}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Form.Item name="contactDate" label={t('form.contact_date')}>
                <DatePicker
                  className="full-width"
                  placeholder={t('form.contact_date')}
                  disabledDate={disabledFutureDate}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="comments" label={t('common.comments')}>
                <TextArea
                  className={styles.textarea}
                  placeholder={t('common.comments')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Flex justify="center">
            <SaveConfirm
              title={`${t('common.add')}?`}
              onConfirm={onAddContacts}
            >
              <Button type="primary">{t('common.add')}</Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
